import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Col, Row } from "react-bootstrap";
import { useBlocker, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TopNavbar from "../topNavbar/TopNavbar";
import Sidebar from "../sidebar/Sidebar";
import { Footer } from "../../Footer";
import { GetAnnouncementGetAllAuctionData } from "../../redux/auth-reducer/auth-service";
import { usePagedList } from "../../hooks/usePagedList";
import { GetToken } from "../../utils/helper/helper";
import { AuctionResultDetails } from "./AuctionResultDetails";
import { ResultsPager } from "./ResultsPager";
import { UnsavedChangesModal } from "./UnsavedChangesModal";
import { formatResultDate } from "./formatResultDate";
import { formatUsd } from "../../utils/helper/money";
import { useBuybackDrafts } from "./useBuybackDrafts";

/*
 * Which auctions are open is the page's state, not the table's (MG-003.1): collapsing an auction
 * with unsaved Team Buyback changes has to ask first, and the table's own expander toggles before
 * telling anyone. So its expander is hidden and the first column is this button, which reads the
 * page's state through a context - the column definitions stay a constant, as the table expects.
 */
// The search is sent a moment after typing stops, not on every keystroke.
const SEARCH_DELAY_MS = 400;

const ExpansionContext = createContext({ isExpanded: () => false, toggle: () => {} });

const ExpandToggle = ({ row }) => {
  const { isExpanded, toggle } = useContext(ExpansionContext);
  const open = isExpanded(row);
  return (
    <button
      type="button"
      className="results-expander"
      aria-label={open ? "Collapse auction details" : "Expand auction details"}
      aria-expanded={open}
      onClick={() => toggle(row)}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d={open ? "M5 9l7 7 7-7" : "M9 5l7 7-7 7"} />
      </svg>
    </button>
  );
};

// #206: the date the auction settled, i.e. its contest's result became final. Null only for the
// operators, who also see the auctions still awaiting their contest's result (a Player is sent
// settled auctions only).
const SettledCell = ({ settledAt }) =>
  settledAt ? formatResultDate(settledAt) : <span className="results-pending">Awaiting result</span>;

const columns = [
  {
    name: "",
    cell: (row) => <ExpandToggle row={row} />,
    width: "60px",
  },
  {
    name: <strong>Auction Name</strong>,
    selector: (row) => row?.auctionName,
    sortable: true,
  },
  {
    name: <strong>Contest Name</strong>,
    selector: (row) => row?.tournamentName,
    sortable: true,
  },
  {
    name: <strong>Sport Name</strong>,
    selector: (row) => row?.sportName,
    sortable: true,
  },
  {
    name: <strong>Total Pool</strong>,
    selector: (row) => row?.totalPoolAmount,
    format: (row) => formatUsd(row?.totalPoolAmount),
    sortable: true,
  },
  {
    name: <strong>Manager Hold</strong>,
    selector: (row) => row?.xcuttaAmount,
    format: (row) => formatUsd(row?.xcuttaAmount),
    sortable: true,
  },
  {
    name: <strong>Net Pool</strong>,
    selector: (row) => row?.netPoolAmount,
    format: (row) => formatUsd(row?.netPoolAmount),
    sortable: true,
  },
  {
    name: <strong>Auction Start Date & Time</strong>,
    selector: (row) => row?.startDate,
    format: (row) => formatResultDate(row?.startDate),
    sortable: true,
    width: "250px",
  },
  // #206: this used to be "Auction End Date & Time", showing the row's endDate - which is the
  // contest's START (the API keeps it so for the app builds in use). The auction's settlement is
  // the date that belongs here.
  {
    name: <strong>Settled</strong>,
    selector: (row) => row?.settledAt ?? "",
    cell: (row) => <SettledCell settledAt={row?.settledAt} />,
    sortable: true,
    width: "250px",
  },
];

/**
 * Auction Results for every role: mounted from /WebAdmin, /SuperAdmin and /Player. Which auctions
 * a role sees is decided by the server (usp_GetAllAuctionAnnouncement scopes rows by the caller),
 * so the page itself is the same for all three. Since #205 a Player is sent only settled auctions;
 * the operators also get the ones awaiting their contest's result, shown as such in Settled.
 */
export const AuctionResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // #236: the player's Auctions list opens a closed auction here with { auctionId, auctionName } in
  // the router state. The page starts searched for its name, with its row open; a Player sees it
  // only once it is settled, like any other row.
  const focus = Number.isInteger(Number(location.state?.auctionId)) ? location.state : null;
  const focusName = typeof focus?.auctionName === "string" ? focus.auctionName.trim() : "";
  const [data, setData] = useState();
  const [searchText, setSearchText] = useState(focusName);
  const [search, setSearch] = useState(focusName);

  // #147: the search used to filter only the loaded page, so an auction on another page was never
  // found. It is now the server's: usp_GetAllAuctionAnnouncement matches the auction, contest, sport
  // and Manager names and counts the matches for the pager.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchText.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // A new fetchPage is a new filter, which usePagedList answers from page 1.
  const fetchPage = useCallback(
    (query) => GetAnnouncementGetAllAuctionData({ ...query, SearchTerm: search }),
    [search]
  );
  const pager = usePagedList(fetchPage);
  const buyback = useBuybackDrafts();
  const { changedCount, discard, discardAll, isDirty, save, saveAll, totals } = buyback;

  // Open auctions by id, so an open row stays open - with its draft - when a search hides it or the
  // pager moves past it and it comes back.
  const [expandedIds, setExpandedIds] = useState(() =>
    focus?.auctionId ? new Set([Number(focus.auctionId)]) : new Set()
  );

  // The state is cleared once read, or a reload would apply it again (as in UserSuper.js).
  useEffect(() => {
    if (!location.state?.auctionId) return;
    navigate(location.pathname + location.search, { replace: true, state: null });
    // Only a new arrival may apply it: the clearing navigate above is itself a location change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);
  // The auction whose collapse is waiting on the "unsaved changes" dialog.
  const [collapsing, setCollapsing] = useState(null);
  const [dialogSaving, setDialogSaving] = useState(false);

  const setExpanded = useCallback((auctionId, open) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (open) next.add(auctionId);
      else next.delete(auctionId);
      return next;
    });
  }, []);

  const isExpanded = useCallback((row) => expandedIds.has(row?.auctionId), [expandedIds]);

  const toggle = useCallback(
    (row) => {
      const auctionId = row.auctionId;
      if (!expandedIds.has(auctionId)) setExpanded(auctionId, true);
      else if (changedCount(auctionId) > 0) setCollapsing(auctionId);
      else setExpanded(auctionId, false);
    },
    [expandedIds, changedCount, setExpanded]
  );

  const expansion = useMemo(() => ({ isExpanded, toggle }), [isExpanded, toggle]);

  // Leaving the page with unsaved changes - a menu item, the top bar, Back - waits on the dialog.
  // Not once the session is gone: sign-out clears the tokens before it navigates, and nothing could
  // be saved without them.
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }) =>
      isDirty && !!GetToken() && currentLocation.pathname !== nextLocation.pathname,
    [isDirty]
  );
  const blocker = useBlocker(shouldBlock);
  const leaving = blocker.state === "blocked";

  // Reloading or closing the tab gets the browser's own warning; forceLogout's hard navigation does
  // not, because it has already cleared the tokens.
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (event) => {
      if (!GetToken()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  const keepEditing = () => {
    if (leaving) blocker.reset();
    setCollapsing(null);
  };

  const discardAndContinue = () => {
    if (leaving) {
      discardAll();
      blocker.proceed();
      return;
    }
    discard(collapsing);
    setExpanded(collapsing, false);
    setCollapsing(null);
  };

  const saveAndContinue = async () => {
    setDialogSaving(true);
    if (leaving) {
      const failures = await saveAll();
      setDialogSaving(false);
      if (!failures.length) {
        blocker.proceed();
      } else {
        // Stay. Each failed auction shows its own error too, but it may be on another page of the
        // list or hidden by the search, so say it here as well.
        blocker.reset();
        toast.error(failures[0]);
      }
      return;
    }
    const auctionId = collapsing;
    const saved = await save(auctionId);
    setDialogSaving(false);
    setCollapsing(null);
    // A failed save leaves the auction open, its selections kept and its error shown above them.
    if (saved) {
      toast.success("Changes saved");
      setExpanded(auctionId, false);
    }
  };

  const dialog = leaving
    ? { teams: totals.teams, auctions: totals.auctions }
    : collapsing !== null
      ? { teams: changedCount(collapsing), auctions: 1 }
      : null;

  function sidebarfun() {
    setData(!data);
  }

  return (
    <>
      <TopNavbar sidebarfun={sidebarfun} />
      <div className="dasboard-group">
        <Sidebar data={data} />
        <div
          className={
            data ? "dashboard-content full-contnet" : "dashboard-content"
          }
        >
          <Row>
            <Col md={6}>
              <h1 className="heading-dash">Auction Results</h1>
            </Col>
          </Row>

          <div className="group_data_table announcement">
            <div className="search-input results-search d-flex align-items-center">
              <input
                type="text"
                className="form-control mb-3 mt-3"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {searchText && (
                <button className="btn btn-clear" onClick={() => setSearchText("")}>
                  <span>&times;</span> {/* This represents the close icon */}
                </button>
              )}
            </div>

            <div>
              {/* The details component is passed as is. Wrapped in an inline arrow it was a new
                  component type on every render, so each keystroke in the search box remounted
                  every open row and fetched its details again. keyField gives rows an identity:
                  without it they are keyed by index, so a search that shifted the rows moved an
                  open panel - with the auction it had loaded - onto another auction's row. */}
              <ExpansionContext.Provider value={expansion}>
                <DataTable
                  columns={columns}
                  data={pager.rows}
                  keyField="auctionId"
                  expandableRows
                  expandableRowsHideExpander
                  expandableRowExpanded={isExpanded}
                  expandableRowsComponent={AuctionResultDetails}
                  expandableRowsComponentProps={{ buyback }}
                />
              </ExpansionContext.Provider>
              <ResultsPager
                page={pager.page}
                totalPages={pager.totalPages}
                perPage={pager.perPage}
                pageSizes={pager.pageSizes}
                onPerPageChange={pager.changePerPage}
                onPrevious={pager.previousPage}
                onNext={pager.nextPage}
              />
            </div>
          </div>
        </div>
        <div className={data ? "footer" : "footer-content"}>
          <Footer />
        </div>
      </div>
      <UnsavedChangesModal
        show={dialog !== null}
        teams={dialog?.teams ?? 0}
        auctions={dialog?.auctions ?? 1}
        saving={dialogSaving}
        onDiscard={discardAndContinue}
        onKeep={keepEditing}
        onSave={saveAndContinue}
      />
    </>
  );
};
